/**
 * Создание ручной рассылки — wizard с выбором канала
 * Дата: 26.02.2026 — PLAN Manual Broadcasts, Итерация 5
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Modal, TenantSelector, LoadingState } from '@/components/ui';
import { manualBroadcastsApi } from '@/api/manualBroadcasts';
import { templatesApi, type Template } from '@/api/templates';
import { ManualTemplatePicker } from '@/components/features/manual-broadcasts/ManualTemplatePicker';
import { SegmentPicker } from '@/components/features/manual-broadcasts/SegmentPicker';
import { RecipientSelector } from '@/components/features/manual-broadcasts/RecipientSelector';
import { ResolvedTemplatePreview } from '@/components/features/templates/ResolvedTemplatePreview';
import { Select } from '@/components/ui';
import { useToast } from '@/hooks/useToast';
import { useTenantScope } from '@/hooks/useTenantScope';
import { filterTemplatesByChannel } from '@/config/channels';
import { isWabaBackedTemplate } from '@/features/templates/templateTaxonomy';
const STEPS = ['Название и сегмент', 'Канал и шаблон', 'Получатели', 'Подтверждение'];

const BROADCAST_CHANNELS: { value: string; label: string; description: string }[] = [
  { value: '', label: 'Авто (FallbackEngine)', description: 'Канал выбирается автоматически по приоритету для каждого пациента' },
  { value: 'whatsapp_waba', label: '📱 WhatsApp (WABA)', description: 'Шаблоны из Wazzup/Meta — одобренные Meta' },
  { value: 'whatsapp_regular', label: '📱 WhatsApp (Regular)', description: 'Наши шаблоны через Wazzup — без одобрения Meta' },
  { value: 'telegram_bot', label: '🤖 Telegram Bot', description: 'Через бота — пациент должен быть привязан' },
  { value: 'max', label: '💬 MAX (VK)', description: 'Через MAX бота — пациент должен быть привязан' },
  { value: 'sms', label: '📝 SMS', description: 'Текстовые SMS — только текст, без кнопок' },
];

function getChannelLabel(channel: string): string {
  if (!channel) return 'Авто (FallbackEngine)';
  const found = BROADCAST_CHANNELS.find(c => c.value === channel);
  return found?.label ?? channel;
}

function getSegmentTypeLabel(type: 'regular' | 'dynamic' | 'lk'): string {
  if (type === 'regular') return 'Обычный';
  if (type === 'dynamic') return 'Динамический';
  if (type === 'lk') return 'ЛК';
  return type;
}

export function ManualBroadcastCreatePage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [segmentType, setSegmentType] = useState<'regular' | 'dynamic' | 'lk'>('regular');
  const [segmentId, setSegmentId] = useState(0);
  const [segmentName, setSegmentName] = useState('');
  const [channelOverride, setChannelOverride] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreviewData, setTemplatePreviewData] = useState<Awaited<ReturnType<typeof templatesApi.preview>> | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setTemplates([]);
      return;
    }
    templatesApi.list(tenantId).then((r) => setTemplates(r.templates ?? [])).catch(() => setTemplates([]));
  }, [tenantId]);

  const filteredTemplates = useMemo(
    () => filterTemplatesByChannel(templates, channelOverride),
    [templates, channelOverride]
  );

  const selectedTemplate = templates.find((t) => t.id === templateId);

  useEffect(() => {
    if (templateId && !filteredTemplates.find(t => t.id === templateId)) {
      setTemplateId('');
    }
  }, [channelOverride, filteredTemplates, templateId]);

  const handleCreate = async () => {
    if (!tenantId) {
      showError('Выберите клинику');
      return;
    }
    if (!name.trim()) {
      showError('Укажите название рассылки');
      return;
    }
    if (segmentId <= 0) {
      showError('Выберите сегмент');
      return;
    }
    if (!templateId) {
      showError('Выберите шаблон');
      return;
    }
    try {
      setLoading(true);
      await manualBroadcastsApi.create({
        name: name.trim(),
        segment_type: segmentType,
        segment_id: segmentId,
        segment_name: segmentName || 'Сегмент',
        template_id: templateId,
        channel_override: channelOverride || undefined,
        selected_phones: [...selectedPhones],
      }, tenantId || undefined);
      showSuccess('Рассылка создана');
      navigate('/manual-broadcasts');
    } catch (e: any) {
      showError(e?.response?.data?.error ?? 'Не удалось создать рассылку');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTemplatePreview = async () => {
    if (!tenantId || !selectedTemplate) return;
    try {
      setTemplatePreviewLoading(true);
      const testVars = Object.fromEntries(
        (selectedTemplate.variables || []).map((variable) => [variable, `[${variable}]`]),
      );
      const preview = await templatesApi.preview(
        selectedTemplate.id,
        testVars,
        tenantId,
        channelOverride || selectedTemplate.messenger || undefined,
      );
      setTemplatePreviewData(preview);
      setTemplatePreviewOpen(true);
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось построить предпросмотр шаблона');
    } finally {
      setTemplatePreviewLoading(false);
    }
  };

  const canNext = () => {
    if (step === 0) return !!tenantId && !!name.trim() && segmentId > 0;
    if (step === 1) return !!templateId;
    if (step === 2) return selectedPhones.size > 0;
    return true;
  };

  return (
    <MainLayout
      headerTitle="Новая рассылка"
      headerActions={
        <Button variant="ghost" onClick={() => navigate('/manual-broadcasts')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Назад
        </Button>
      }
    >
      <div className="p-6 max-w-2xl mx-auto">
        {isSuperAdmin && (
          <div className="mb-4">
            <TenantSelector
              value={selectedTenantId || ''}
              onChange={setSelectedTenant}
              required
              testId="manual-broadcast-create-tenant-selector"
            />
          </div>
        )}
        <div className="flex gap-2 mb-6">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded ${i <= step ? 'bg-primary-500' : 'bg-neutral-200'}`}
              title={s}
            />
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{STEPS[step]}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 0 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Название рассылки</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Например: Напоминание о приёме"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Тип сегмента</label>
                  <Select
                    value={segmentType}
                    onChange={(e) => {
                      setSegmentType(e.target.value as 'regular' | 'dynamic' | 'lk');
                      setSegmentId(0);
                      setSegmentName('');
                    }}
                    options={[
                      { value: 'regular', label: 'Обычный сегмент' },
                      { value: 'dynamic', label: 'Динамический сегмент' },
                      { value: 'lk', label: 'ЛК сегмент' },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Сегмент</label>
                  {tenantId ? (
                    <SegmentPicker
                      segmentType={segmentType}
                      segmentId={segmentId}
                      segmentName={segmentName}
                      tenantId={tenantId || undefined}
                      onSelect={(id, name, _type) => {
                        setSegmentId(id);
                        setSegmentName(name);
                        setSelectedPhones(new Set());
                      }}
                    />
                  ) : (
                    <div className="text-sm text-neutral-500 py-2">Сначала выберите клинику.</div>
                  )}
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Канал отправки</label>
                  <select
                    value={channelOverride}
                    onChange={(e) => setChannelOverride(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  >
                    {BROADCAST_CHANNELS.map((ch) => (
                      <option key={ch.value} value={ch.value}>
                        {ch.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">
                    {BROADCAST_CHANNELS.find(c => c.value === channelOverride)?.description}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Шаблон {filteredTemplates.length !== templates.length && (
                      <span className="text-neutral-400 font-normal">
                        ({filteredTemplates.length} из {templates.length} для выбранного канала)
                      </span>
                    )}
                  </label>
                  <ManualTemplatePicker
                    templates={filteredTemplates}
                    value={templateId}
                    onChange={(id) => setTemplateId(id)}
                  />
                  {selectedTemplate && (
                    <div className="mt-2 flex justify-end">
                      <Button variant="secondary" size="sm" onClick={handleOpenTemplatePreview} disabled={templatePreviewLoading}>
                        <Eye className="w-4 h-4 mr-2" />
                        {templatePreviewLoading ? 'Строим предпросмотр…' : 'Посмотреть предпросмотр'}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}

            {step === 2 && (
              <RecipientSelector
                segmentType={segmentType}
                segmentId={segmentId}
                tenantId={tenantId || undefined}
                selectedPhones={selectedPhones}
                onSelectionChange={setSelectedPhones}
                previewRequestFactory={(patient) => {
                  if (!templateId || !segmentId) return null;
                  return {
                    phone: patient.phone,
                    segment_type: segmentType,
                    segment_id: segmentId,
                    template_id: templateId,
                    channel_override: channelOverride || undefined,
                  };
                }}
              />
            )}

            {step === 3 && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-neutral-500">Название:</span>
                  <span className="font-medium">{name}</span>
                  <span className="text-neutral-500">Сегмент:</span>
                  <span className="font-medium">{segmentName} <span className="text-neutral-400">({getSegmentTypeLabel(segmentType)})</span></span>
                  <span className="text-neutral-500">Получатели:</span>
                  <span className="font-medium">{selectedPhones.size} чел.</span>
                  <span className="text-neutral-500">Канал:</span>
                  <span className="font-medium">{getChannelLabel(channelOverride)}</span>
                  <span className="text-neutral-500">Шаблон:</span>
                  <span className="font-medium">{selectedTemplate?.name ?? templateId}</span>
                </div>
                {channelOverride === '' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                    Канал будет выбран автоматически для каждого пациента по приоритету:
                    WABA &rarr; TG Client &rarr; TG Bot &rarr; SMS
                  </div>
                )}
                {(channelOverride === 'telegram_bot' || channelOverride === 'max') && selectedTemplate?.buttons && selectedTemplate.buttons.length > 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                    Шаблон содержит кнопки — они будут отправлены как inline keyboard.
                    Автоответы на нажатия кнопок работают через систему автоответов.
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button
                variant="secondary"
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Назад
              </Button>
              {step < STEPS.length - 1 ? (
                <Button variant="primary" onClick={() => setStep(step + 1)} disabled={!canNext()}>
                  Далее
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button variant="primary" onClick={handleCreate} disabled={loading}>
                  {loading ? 'Создание…' : 'Создать рассылку'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <Modal
        isOpen={templatePreviewOpen}
        onClose={() => setTemplatePreviewOpen(false)}
        title="Предпросмотр шаблона"
        size="lg"
      >
        {templatePreviewLoading ? (
          <LoadingState text="Загрузка предпросмотра..." size="sm" inline />
        ) : templatePreviewData && selectedTemplate ? (
          <ResolvedTemplatePreview
            preview={templatePreviewData.preview}
            wabaPreview={templatePreviewData.waba_preview}
            buttons={templatePreviewData.buttons}
            variablesMissing={templatePreviewData.variables_missing}
            channel={channelOverride || selectedTemplate.messenger || undefined}
            isWabaTemplate={isWabaBackedTemplate(selectedTemplate)}
            testId="manual-broadcast-create-template-preview"
          />
        ) : null}
      </Modal>
    </MainLayout>
  );
}
